import React from 'react';
import { connect } from 'react-redux';
import flow from 'lodash/fp/flow';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import MenuList from './MenuList';
import ScreenOverlay from '../@common/ScreenOverlay';
import { remCalc } from '../../util/style-util';
import { getGradeLevelDescription, getNewLink } from '../../util/workbook-util';
import WorkbookSelector from './WorkbookSelector';
import WorkbooksMenu from './WorkbooksMenu';
import LanguageDropdown from '../@common/Dropdowns/LanguageDropdown';
import { translate } from '../../translations/translator';
import { BadgeType, UserSetting, Workbook, AppStoreState } from 'types';
import { UserState, getBadgeTypeForWorkbook } from 'state/modules/user';
import { toggleSlideoutMenu, toggleMenuByType, toggleSubmenu } from 'state/modules/ui/actions';
import { changeAppLocale } from 'state/modules/app';
import { getUsersWorkbooks } from 'state/modules/entity/thunks';
import { getTours, dequeueTour, queueTour } from 'state/modules/tour';
import { saveUserSetting } from 'state/modules/user/thunks';
import { NavigationItems } from './NavigationItems';
import { FONT_GSS_FUTURA } from 'themes/default';
import { logoutUser } from 'state/modules/user/actions';
import { selectWorkbook } from 'state/modules/app';
import { getLicenseIsTrial } from 'state/modules/user/selectors/user-selectors';

const translateApps = key => translate('apps', key);

type Props = {
  badgeType: BadgeType;
  basePath: string;
  bookColor: string;
  children: any;
  currentWorkbook: Workbook;
  isClickAwayOnly: boolean;
  lastTeachersGuide: Workbook;
  lastWorkbook: Workbook;
  match: any;
  renderWorkbookBadge: boolean;
  selectedSubmenu: string;
  uiState: any;
  user: UserState;
  workbooks: { [id: string]: Workbook };
  changeAppLocale: (locale: string) => void;
  dequeueTour: typeof dequeueTour;
  getTours: () => void;
  getUsersWorkbooks: () => void;
  queueTour: typeof queueTour;
  saveUserSetting: (type: string, setting: UserSetting) => void;
  toggleSlideoutMenu: typeof toggleSlideoutMenu;
  toggleSubmenu: (submenuName: string) => void;
  toggleWorkbooksMenu: (isOpen?: boolean) => void;
} & any;

const MENU_WIDTH = 328;

const SlideoutMenu = (props: Props) => {
  const handleWorkbookClick = workbook => {
    props.selectWorkbook(workbook);
    props.toggleWorkbooksMenu();
  };

  const handleToggleWorkbooksMenu = () => {
    if (Object.keys(props.workbooks).length > 0) {
      props.toggleWorkbooksMenu();
    }
  };

  const toggleMenu = () => props.toggleSlideoutMenu(!props.uiState.globalMenuOpen);

  const { title, uiState, basePath, selectedSubmenu, toggleSubmenu, user, currentWorkbook }: any =
    props;

  const {
    menuWrapper,
    showMenu,
    hideMenu,
    slideoutMenu,
    languageDiv,
    listContainer,
    plusLiveInsightLink,
    dividingLine,
    menuContainer
  } = useStyles()({
    width: MENU_WIDTH,
    bookColor: currentWorkbook?.primaryColor,
    secondaryColor: currentWorkbook?.secondaryColor
  });

  const badgeType = getBadgeTypeForWorkbook(user.licenses, user.clickAways, props.currentWorkbook);
  const userId = user.me ? user.me.id : 0;

  return (
    <section className={menuContainer}>
      <div className={clsx(menuWrapper, uiState.globalMenuOpen ? showMenu : hideMenu)}>
        <nav className={slideoutMenu}>
          <WorkbookSelector
            renderWorkbookBadge={!!badgeType}
            badgeType={badgeType}
            title={title ? title : props.currentWorkbook?.title}
            imageUrl={props.currentWorkbook?.combinedCoverImageUrl}
            shouldHaveArrow={props.workbooks.length > 0}
            openWorkbookMenu={handleToggleWorkbooksMenu}
            gradeLevelDescription={getGradeLevelDescription(props.currentWorkbook?.gradeLevel)}
          />
          <div className={listContainer}>
            <MenuList
              show={!uiState.workbooksMenuOpen}
              menuItems={NavigationItems(
                props,
                badgeType === 'clickAway',
                user.isAuditor || props.hasValidTrialLicense
              )}
              toggleMenu={toggleMenu}
              basePath={basePath}
              activeColor={props.currentWorkbook?.primaryColor}
              keepSubmenuState={uiState.globalMenuOpen}
              selectedSubmenu={selectedSubmenu}
              toggleSubmenu={toggleSubmenu}
            />
            <WorkbooksMenu
              show={uiState.workbooksMenuOpen}
              workbooks={props.workbooks}
              onWorkbookClick={handleWorkbookClick}
              userId={userId}
              licenses={user.licenses}
              clickAways={user.clickAways}
            />
          </div>
          <div style={{ marginBottom: 30 }}>
            <hr className={dividingLine} />
            <div style={{ textAlign: 'center' }}>
              <div
                className={plusLiveInsightLink}
                onClick={() =>
                  window.open(
                    getNewLink(
                      props.currentWorkbook?.bookCode,
                      props.currentWorkbook?.edition,
                      'pli',
                      userId
                    ),
                    '_blank'
                  )
                }
              >
                {translateApps('plusLive')}
              </div>
            </div>
            <div className={languageDiv}>
              <LanguageDropdown labelFontColor="#FFFFFF" width="200px" />
            </div>
          </div>
        </nav>
      </div>
      <ScreenOverlay show={uiState.globalMenuOpen} onClick={toggleMenu} />
      {props.children}
    </section>
  );
};

const useStyles = () =>
  makeStyles(() => ({
    menuContainer: ({ secondaryColor }: any) => ({
      height: '100%',
      width: '100%',
      overflowY: 'auto',
      backgroundColor: secondaryColor
    }),
    menuWrapper: {
      position: 'absolute',
      height: '93%',
      transition: 'all .30s ease-in-out',
      '@media print': {
        display: 'none'
      },
      paddingTop: remCalc(60)
    },
    showMenu: {
      left: 0
    },
    hideMenu: ({ width }: any) => ({
      left: `-${remCalc(width)}`
    }),
    slideoutMenu: {
      backgroundColor: '#202020',
      backgroundSize: 'cover',
      color: 'white',
      height: '95vh',
      width: '328px',
      zIndex: 10,
      position: 'absolute',
      display: 'flex',
      flexFlow: 'column nowrap',
      borderTop: '3px solid #000000'
    },
    languageDiv: {
      paddingTop: '10px',
      fontFamily: FONT_GSS_FUTURA,
      fontSize: remCalc(16),
      display: 'flex'
    },
    menuEdge: {
      position: 'absolute',
      right: 0,
      top: 0,
      height: '100%',
      width: '5px'
    },
    menuLogo: {
      width: '100%',
      marginTop: '5%',
      bottom: remCalc(20),
      padding: remCalc(15),
      borderTop: '1px solid black'
    },
    listContainer: {
      flex: '2 auto',
      overflowY: 'auto',
      overflowX: 'hidden',
      position: 'relative',
      maxHeight: '550px',
      '::-webkit-scrollbar': {
        backgroundColor: 'rgba(32, 32, 34, 1)'
      },
      '::-webkit-scrollbar-thumb': {
        borderRadius: remCalc(15),
        backgroundColor: 'rgba(85, 85, 85, 1)',
        backgroundClip: 'content-box',
        border: '5px solid transparent'
      }
    },
    plusLiveInsightLink: ({ bookColor }: any) => ({
      color: '#FFFFFF',
      margin: 'auto',
      textDecoration: 'none',
      '&:link': {
        color: '#FFFFFF',
        textDecoration: 'none'
      },
      '&:visited': {
        color: '#FFFFFF',
        textDecoration: 'none'
      },
      '&:hover': {
        color: bookColor,
        textDecoration: 'none',
        cursor: 'pointer'
      },
      '&:focus': {
        color: bookColor,
        textDecoration: 'none'
      },
      '&:active': {
        color: bookColor,
        textDecoration: 'none'
      }
    }),
    dividingLine: {
      borderTop: '1px solid #FFFFFF',
      opacity: 0.3,
      width: '210px'
    }
  }));

const mapStateToProps = (state: AppStoreState) => ({
  uiState: state.ui,
  user: state.user,
  pageSelectorMenuOpen: state.ui.pageSelectorMenuOpen,
  currentWorkbook: state.app.currentWorkbook,
  workbooks: state.entity.workbooks,
  lastTeachersGuide: state.entity.lastTeachersGuide,
  lastWorkbook: state.entity.lastWorkbook,
  hasValidTrialLicense: getLicenseIsTrial(state)
});

const withRedux = connect(mapStateToProps, {
  changeAppLocale,
  dequeueTour,
  getTours,
  getUsersWorkbooks,
  saveUserSetting,
  toggleSlideoutMenu,
  toggleWorkbooksMenu: toggleMenuByType('workbooks'),
  toggleSubmenu,
  queueTour,
  logoutUser,
  selectWorkbook
});

export default flow(withRedux)(SlideoutMenu as any) as any;
